import classNames from "classnames/bind";
import React from "react";
import { EmptyState } from "../../../EmptyState";
import { Icon, IconLoading } from "../../../Icon";
import type { FileRendererProps } from "../types";
import styles from "./FileRenderer.module.scss";

const cx = classNames.bind(styles);

const FileRenderer = ({
  fileType,
  fileMimeType,
  fileExtension,
  fileObjectUrl,
  isLoading,
  isBusy,
  errorMessage,
  className,
}: FileRendererProps) => {
  const unsupportedState = (
    <EmptyState
      title="Unsupported Preview Format"
      icon={<Icon variant="eye-slash" />}
      suggestion={`No preview available${fileExtension ? ` for .${fileExtension} files` : ""}`}
    />
  );

  if (isLoading || isBusy) {
    return <IconLoading speed="fast" />;
  }

  if (errorMessage) {
    return (
      <EmptyState
        title="Preview unavailable"
        variant="error"
        icon={<Icon variant="warning" />}
        suggestion={`Failed to fetch file: ${errorMessage}`}
      />
    );
  }

  if (!fileObjectUrl) {
    return (
      <EmptyState
        title="Preview unavailable"
        variant="error"
        icon={<Icon variant="warning" />}
        suggestion="Failed to fetch file"
      />
    );
  }

  switch (fileType) {
    case "IMAGE":
      return (
        <img
          className={cx("image-render", className)}
          src={fileObjectUrl}
          alt=""
        />
      );
    case "VIDEO":
      return (
        <video
          controls
          muted
          className={cx("video-render", className)}
        >
          <source
            src={fileObjectUrl}
            type={fileMimeType ?? undefined}
          />
          {unsupportedState}
        </video>
      );
    case "AUDIO":
      return (
        <audio
          controls
          className={className}
        >
          <source
            src={fileObjectUrl}
            type={fileMimeType ?? undefined}
          />
          {unsupportedState}
        </audio>
      );
    default:
      return unsupportedState;
  }
};

export default FileRenderer;
